import { useState, useRef, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
// Market data actions
import {
  fetchTopMovers,
  fetchTopGainers,
  fetchTopLosers,
  fetchMostActive,
} from '../../redux/stockData/marketSnapshot/marketSnapshotActions';

// Watchlist actions
import {
  fetchWatchlistThunk,
  followStockThunk,
  unfollowStockThunk
} from '../../redux/user/watchlist/watchlistActions';

// Sparkline chart data — same batched/cached fetch the dashboard's watchlist
// cards already use successfully.
import { fetchWatchlistSimpleChartsThunk } from '../../redux/stockData/nasdaq/stockDataActions';



// Lazy load components for better performance
const ScrollableStockRow = lazy(() => import('../../components/stocks/scrollableStockRow'));
const MarketOverviewSection = lazy(() => import('../../components/stocks/marketOverviewSection'));
const StockCategoryRows = lazy(() => import('../../components/stocks/stockCategoryRows'));
const LiveTickerBar = lazy(() => import('../../components/stocks/liveTickerBar'));


// Loading skeleton component
const LoadingSkeleton = () => (
  <div className="bg-gray-200 dark:bg-gray-800/50 rounded-lg h-48 animate-pulse" />
);

// Matches scrollableStockRow's real card shape (stockCard.jsx's default
// size: w-56 sm:w-64 h-44 sm:h-48, rounded-lg, logo+symbol header then a
// price line) so the swap from skeleton to real cards doesn't jump.
const StockCardSkeleton = () => (
  <div className="flex-shrink-0 w-56 sm:w-64 h-44 sm:h-48 bg-gray-100 dark:bg-gray-800/95 rounded-lg border border-indigo-200 dark:border-indigo-900/50 p-3 sm:p-4 animate-pulse">
    <div className="flex items-start gap-2 mb-3">
      <div className="w-9 h-9 rounded-full bg-gray-300 dark:bg-gray-700/70 flex-shrink-0" />
      <div className="flex-1 space-y-1.5 pt-0.5">
        <div className="h-4 bg-gray-300 dark:bg-gray-700/70 rounded w-12" />
        <div className="h-3 bg-gray-200 dark:bg-gray-700/50 rounded w-20" />
      </div>
    </div>
    <div className="h-5 bg-gray-300 dark:bg-gray-700/70 rounded w-20 mb-2" />
    <div className="h-3 bg-gray-200 dark:bg-gray-700/50 rounded w-16" />
  </div>
);

const StockRowSkeleton = () => (
  <div className="flex gap-3 sm:gap-4 overflow-hidden">
    {Array.from({ length: 6 }).map((_, i) => (
      <StockCardSkeleton key={i} />
    ))}
  </div>
);

const StocksOverviewPageSkeleton = () => (
  <div className="space-y-8">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i}>
        <div className="h-6 bg-gray-300 dark:bg-gray-800/70 rounded w-40 mb-4 animate-pulse" />
        <StockRowSkeleton />
      </div>
    ))}
  </div>
);

const StocksOverviewPage = () => {
  const { t } = useTranslation();
  // Declare all refs
  const trendingRef = useRef(null);
  const topGainersRef = useRef(null);
  const topLosersRef = useRef(null);
  const mostActiveRef = useRef(null);
  const techRef = useRef(null);
  const financeRef = useRef(null);

  const dispatch = useDispatch();

  // Get data from Redux store
  const marketSnapshot = useSelector((state) => state.marketSnapshot || {});
  const {
    topMovers = [],
    topGainers = [],
    topLosers = [],
    mostActive = [],
  } = marketSnapshot;

  // This page's own loading/error state -- deliberately NOT the shared
  // `marketSnapshot.status`/`error` fields. Those are only ever set by the
  // `fetchMarketOverview` thunk, which this page stopped dispatching itself
  // (a prior fix removed it as redundant dead weight here) but the
  // Dashboard still dispatches for its own Indexes tab. Because it's one
  // global Redux slice, a `fetchMarketOverview()` call still in flight from
  // a *previous* page (e.g. Dashboard -> Stocks Overview via client-side
  // nav) can resolve *after* this page has already mounted and set
  // `status: 'failed'` here too -- blanking out this page's own real,
  // successfully-loaded data behind a full-page error, even though none of
  // this page's own 4 fetches failed. A full browser refresh resets the
  // Redux store, so the stale in-flight request no longer exists and the
  // bug disappears -- exactly the "works on refresh" symptom reported.
  const [pageStatus, setPageStatus] = useState('loading');
  const [pageError, setPageError] = useState(null);

  // ✅ FINAL FIX: Use shallowEqual for watchlist state
  const watchlistState = useSelector(
    (state) => ({
      items: state.watchlist?.items || [],
      stocks: state.watchlist?.stocks || [],
      loading: state.watchlist?.loading || false
    }),
    shallowEqual
  );

  // Get user ID from auth state
  const userId = useSelector((state) => state.auth?.user?.id);

  // Memoize the formatNumber function
  const formatNumber = useCallback((num) => {
    if (num === null || num === undefined || isNaN(num)) {
      return 'N/A';
    }
    
    const numValue = parseFloat(num);
    if (isNaN(numValue)) return 'N/A';
    
    if (numValue >= 1e12) return (numValue / 1e12).toFixed(1) + 'T';
    if (numValue >= 1e9) return (numValue / 1e9).toFixed(1) + 'B';
    if (numValue >= 1e6) return (numValue / 1e6).toFixed(1) + 'M';
    if (numValue >= 1e3) return (numValue / 1e3).toFixed(1) + 'K';
    return numValue.toFixed(2);
  }, []);

  // Transform stock data with price history extraction
  const transformStockData = useCallback((apiData) => {
    if (!apiData || !Array.isArray(apiData)) return { stocks: [], priceHistory: {} };
    
    const stocks = [];
    const priceHistory = {};
    
    apiData.forEach(item => {
      if (!item) return;
      
      const symbol = item.ticker?.trim();
      if (!symbol) return;
      
      const price = parseFloat(item.last_price) || 0;
      
      // Filter out delisted stocks
      if (price <= 0) return;
      
      const stockData = item.stock || {};
      const change = parseFloat(item.change) || 0;
      const changePercent = parseFloat(item.change_percent) || 0;
      const volume = parseInt(item.volume) || 0;
      
      let name = stockData.name?.trim();
      if (!name || name === '') {
        name = symbol;
      }
      
      let marketCap = 'N/A';
      if (stockData.market_cap && stockData.market_cap !== null) {
        const mcap = parseFloat(stockData.market_cap);
        if (!isNaN(mcap) && mcap > 0) {
          marketCap = formatNumber(mcap);
        }
      }
      
      let category = stockData.industry?.trim();
      if (!category || category === '' || category === 'null') {
        category = 'Other';
      }
      
      let description = stockData.description?.trim();
      if (!description || description === '' || description === 'null') {
        description = `${name} stock information`;
      } else if (description.length > 200) {
        description = description.substring(0, 200) + '...';
      }
      
      stocks.push({
        symbol,
        name,
        price,
        change,
        changePercent,
        volume,
        marketCap,
        category,
        description,
        id: stockData.id
      });
    });

    // Sparklines: the market-snapshot endpoints these categories come from
    // (top-movers/gainers/losers/most-active) never returned a
    // `price_history` field on each item -- it isn't part of that response
    // schema at all, so this used to silently build an empty priceHistory
    // map every time. Real history is fetched separately (see the
    // useEffect below, using the same batched/cached chart fetch the
    // dashboard's watchlist cards already rely on) and merged in by symbol.
    return { stocks, priceHistory };
  }, [formatNumber]);

  // Same multi-shape normaliser UnifiedStockerDashboard.jsx uses for the
  // exact same `watchlistSimpleCharts[symbol]` payload shape.
  const extractSparklinePrices = useCallback((raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.prices)) return raw.prices;
    if (Array.isArray(raw.data?.prices)) return raw.data.prices;
    if (Array.isArray(raw.data?.data)) return raw.data.data;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.results)) return raw.results;
    return [];
  }, []);

  const watchlistSimpleCharts = useSelector((state) => state.stockData?.watchlistSimpleCharts || {});

  // Memoize transformed data
  const transformedData = useMemo(() => {
    const topMoversData = transformStockData(topMovers);
    const topGainersData = transformStockData(topGainers);
    const topLosersData = transformStockData(topLosers);
    const mostActiveData = transformStockData(mostActive);

    // Real price history, keyed by symbol, from the batched chart fetch below.
    const combinedPriceHistory = {};
    for (const sym of Object.keys(watchlistSimpleCharts)) {
      // `/stocks_charts/{ticker}/data` returns `{ data: [{period_close, avg_close, ...}] }`
      // -- same field names Sparkline's own internal normaliser already
      // knows about (`period_close ?? avg_close ?? close ?? c ?? price`).
      const prices = extractSparklinePrices(watchlistSimpleCharts[sym])
        .map((p) => (typeof p === 'number' ? p : parseFloat(p?.period_close ?? p?.avg_close ?? p?.close ?? p?.c ?? p?.price)) || 0)
        .filter((val) => val > 0);
      if (prices.length > 1) combinedPriceHistory[sym] = prices;
    }

    return {
      topMovers: topMoversData.stocks,
      topGainers: topGainersData.stocks,
      topLosers: topLosersData.stocks,
      mostActive: mostActiveData.stocks,
      priceHistory: combinedPriceHistory
    };
  }, [topMovers, topGainers, topLosers, mostActive, transformStockData, watchlistSimpleCharts, extractSparklinePrices]);

  // Fetch real sparkline history for the symbols actually shown on this page,
  // capped to keep the batch request bounded (the fetch thunk itself is
  // already concurrency-limited and TTL-cached).
  useEffect(() => {
    const symbols = Array.from(new Set([
      ...topMovers.map((i) => i.ticker),
      ...topGainers.map((i) => i.ticker),
      ...topLosers.map((i) => i.ticker),
      ...mostActive.map((i) => i.ticker),
    ].filter(Boolean))).slice(0, 60);

    if (symbols.length > 0) {
      dispatch(fetchWatchlistSimpleChartsThunk({ symbols, days: 30 }));
    }
  }, [topMovers, topGainers, topLosers, mostActive, dispatch]);

  // ✅ FIXED: Create stable symbol-to-ID map
  const stockSymbolToIdMap = useMemo(() => {
    const map = new Map();
    
    const addFromArray = (arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach(item => {
        if (item?.ticker && item?.stock?.id) {
          map.set(item.ticker.trim(), item.stock.id);
        }
      });
    };
    
    addFromArray(topMovers);
    addFromArray(topGainers);
    addFromArray(topLosers);
    addFromArray(mostActive);
    
    return map;
  }, [topMovers, topGainers, topLosers, mostActive]);

  // ✅ FIXED: Stable watchlist symbols Set
  const watchlistSymbols = useMemo(() => {
    // Combine watchlist items and legacy IDs
    const allWatchlistIds = new Set([
      ...watchlistState.stocks,
      ...(watchlistState.items.map(item => item?.id).filter(Boolean))
    ]);

    const symbolsArray = Array.from(stockSymbolToIdMap.entries())
      .filter(([symbol, stockId]) => allWatchlistIds.has(stockId))
      .map(([symbol]) => symbol);
    
    return new Set(symbolsArray.sort());
  }, [stockSymbolToIdMap, watchlistState]);

  // Memoize categories
  const categories = useMemo(() => {
    const { 
      topMovers: transformedTopMovers, 
      topGainers: transformedTopGainers, 
      topLosers: transformedTopLosers, 
      mostActive: transformedMostActive 
    } = transformedData;
    
    // Combine all stock data for categorization
    const allStocks = [
      ...transformedTopMovers,
      ...transformedTopGainers,
      ...transformedTopLosers,
      ...transformedMostActive
    ];
    
    // Remove duplicates by symbol
    const uniqueStocksMap = new Map();
    allStocks.forEach(stock => {
      if (!uniqueStocksMap.has(stock.symbol)) {
        uniqueStocksMap.set(stock.symbol, stock);
      }
    });
    const uniqueStocks = Array.from(uniqueStocksMap.values());
    
    return {
      Trending: transformedTopMovers.slice(0, 20),
      Technology: uniqueStocks.filter(s => {
        const cat = s.category?.toLowerCase() || '';
        return cat.includes('tech') || 
               cat.includes('software') ||
               cat.includes('computer') ||
               cat.includes('semiconductor') ||
               cat.includes('internet') ||
               cat.includes('telecom');
      }),
      Finance: uniqueStocks.filter(s => {
        const cat = s.category?.toLowerCase() || '';
        return cat.includes('finance') || 
               cat.includes('bank') ||
               cat.includes('investment') ||
               cat.includes('insurance') ||
               cat.includes('credit') ||
               cat.includes('financial');
      }),
      Healthcare: uniqueStocks.filter(s => {
        const cat = s.category?.toLowerCase() || '';
        return cat.includes('healthcare') || 
               cat.includes('pharmaceutical') ||
               cat.includes('biotech') ||
               cat.includes('medical') ||
               cat.includes('drug') ||
               cat.includes('health');
      }),
      Energy: uniqueStocks.filter(s => {
        const cat = s.category?.toLowerCase() || '';
        return cat.includes('energy') || 
               cat.includes('oil') ||
               cat.includes('gas') ||
               cat.includes('petroleum') ||
               cat.includes('renewable') ||
               cat.includes('utility');
      }),
      Consumer: uniqueStocks.filter(s => {
        const cat = s.category?.toLowerCase() || '';
        return cat.includes('consumer') || 
               cat.includes('retail') ||
               cat.includes('food') ||
               cat.includes('beverage') ||
               cat.includes('apparel') ||
               cat.includes('automotive');
      }),
      Watchlist: uniqueStocks.filter(s => watchlistSymbols.has(s.symbol)),
      TopGainers: transformedTopGainers,
      TopLosers: transformedTopLosers,
      MostActive: transformedMostActive
    };
  }, [transformedData, watchlistSymbols]);

  // Memoize the toggleWatchlist function
  const toggleWatchlist = useCallback(async (symbol) => {
    if (!userId) {
      console.warn('User must be logged in to manage watchlist');
      return;
    }

    const stockId = stockSymbolToIdMap.get(symbol);
    if (!stockId) {
      console.warn(`Stock ID not found for symbol: ${symbol}`);
      return;
    }

    const allWatchlistIds = new Set([
      ...watchlistState.stocks,
      ...(watchlistState.items.map(item => item?.id).filter(Boolean))
    ]);

    const isInWatchlist = allWatchlistIds.has(stockId);
    
    try {
      if (isInWatchlist) {
        await dispatch(unfollowStockThunk({ userId, stockId })).unwrap();
      } else {
        await dispatch(followStockThunk({ userId, stockId })).unwrap();
      }
    } catch (error) {
      console.warn('Error toggling watchlist:', error);
    }
  }, [userId, stockSymbolToIdMap, watchlistState, dispatch]);

  // Fetch data on mount
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setPageStatus('loading');
      setPageError(null);

      // allSettled, not all -- one category legitimately having zero rows
      // (e.g. top-losers) shouldn't block the other three from rendering.
      // Each row already degrades gracefully on its own (ScrollableStockRow
      // renders nothing for an empty category) -- only show a full-page
      // error if every single category failed.
      const results = await Promise.allSettled([
        dispatch(fetchTopMovers()).unwrap(),
        dispatch(fetchTopGainers()).unwrap(),
        dispatch(fetchTopLosers()).unwrap(),
        dispatch(fetchMostActive()).unwrap(),
      ]);

      if (cancelled) return;

      const allFailed = results.every((r) => r.status === 'rejected');
      if (allFailed) {
        const reason = results[0].reason;
        const message =
          typeof reason === 'string'
            ? reason
            : reason?.error?.message || reason?.detail || reason?.message || 'Unable to load market data. Please try again.';
        setPageStatus('failed');
        setPageError(message);
      } else {
        setPageStatus('succeeded');
      }

      if (userId) {
        dispatch(fetchWatchlistThunk(userId));
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [dispatch, userId]);

  return (
    <div className="relative w-full min-h-screen">
      <div className="w-full bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900/50 overflow-x-hidden">
        <div className="w-full">
          <div className="max-w-[1600px] mx-auto px-2 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6 text-gray-900 dark:text-slate-100 w-full">

            {/* LOADING STATE */}
            {pageStatus === 'loading' && <StocksOverviewPageSkeleton />}

            {/* ERROR STATE -- only when every category failed, not just one */}
            {pageStatus === 'failed' && pageError && (
              <div className="flex items-center justify-center min-h-[60vh] px-4">
                <div className="text-center">
                  <div className="text-red-500 text-4xl sm:text-5xl lg:text-6xl mb-4">⚠️</div>
                  <p className="text-base sm:text-lg lg:text-xl text-red-600 dark:text-red-400">{t('stocks.overviewPage.errorLoadingMarketData')}</p>
                  <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm sm:text-base">{pageError}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm sm:text-base"
                  >
                    {t('stocks.common.retry')}
                  </button>
                </div>
              </div>
            )}

            {/* MAIN CONTENT */}
            {pageStatus !== 'loading' && pageStatus !== 'failed' && (
              <Suspense fallback={<LoadingSkeleton />}>
                <div className="space-y-4 sm:space-y-6">
                  {/* LIVE TICKER BAR */}
                  <div className="mb-3 sm:mb-4">
                    <LiveTickerBar stocks={categories.Trending.slice(0, 10)} />
                  </div>

                  {/* MARKET OVERVIEW */}
                  <MarketOverviewSection />

                  {/* TRENDING NOW */}
                  <ScrollableStockRow
                    title={t('stocks.rows.trendingNow')}
                    stocks={categories.Trending}
                    scrollRef={trendingRef}
                    watchlist={watchlistSymbols}
                    toggleWatchlist={toggleWatchlist}
                    formatNumber={formatNumber}
                    showFeatured={false}
                    stockPriceHistory={transformedData.priceHistory}
                  />

                  {/* MARKET LEADERS */}
                  <ScrollableStockRow
                    title={t('stocks.rows.marketLeaders')}
                    stocks={categories.TopGainers}
                    scrollRef={topGainersRef}
                    watchlist={watchlistSymbols}
                    toggleWatchlist={toggleWatchlist}
                    formatNumber={formatNumber}
                    stockPriceHistory={transformedData.priceHistory}
                  />

                  {/* MARKET DECLINES */}
                  <ScrollableStockRow
                    title={t('stocks.rows.marketDeclines')}
                    stocks={categories.TopLosers}
                    scrollRef={topLosersRef}
                    watchlist={watchlistSymbols}
                    toggleWatchlist={toggleWatchlist}
                    formatNumber={formatNumber}
                    stockPriceHistory={transformedData.priceHistory}
                  />

                  {/* HIGH VOLUME */}
                  <ScrollableStockRow
                    title={t('stocks.rows.highVolume')}
                    stocks={categories.MostActive}
                    scrollRef={mostActiveRef}
                    watchlist={watchlistSymbols}
                    toggleWatchlist={toggleWatchlist}
                    formatNumber={formatNumber}
                    stockPriceHistory={transformedData.priceHistory}
                  />

                  {/* TECH SECTOR */}
                  {categories.Technology.length > 0 && (
                    <ScrollableStockRow
                      title={t('stocks.rows.techSector')}
                      stocks={categories.Technology}
                      scrollRef={techRef}
                      watchlist={watchlistSymbols}
                      toggleWatchlist={toggleWatchlist}
                      formatNumber={formatNumber}
                      stockPriceHistory={transformedData.priceHistory}
                    />
                  )}

                  {/* FINANCIAL SERVICES */}
                  {categories.Finance.length > 0 && (
                    <ScrollableStockRow
                      title={t('stocks.rows.financialServices')}
                      stocks={categories.Finance}
                      scrollRef={financeRef}
                      watchlist={watchlistSymbols}
                      toggleWatchlist={toggleWatchlist}
                      formatNumber={formatNumber}
                      stockPriceHistory={transformedData.priceHistory}
                    />
                  )}

                  {/* OTHER CATEGORIES */}
                  <StockCategoryRows
                    categories={categories}
                    watchlist={watchlistSymbols}
                    toggleWatchlist={toggleWatchlist}
                    formatNumber={formatNumber}
                    skipKeys={["Trending", "TopGainers", "TopLosers", "MostActive", "Technology", "Finance"]}
                    stockPriceHistory={transformedData.priceHistory}
                  />
                </div>
              </Suspense>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default StocksOverviewPage;